import React from 'react';
import styled from 'styled-components';

// --- STYLED COMPONENTS (Clean Light Theme) ---

const CTAWrapper = styled.section`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  color: #0f172a;
  padding: 20px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  border-top: 1px solid #bae6fd;
  border-bottom: 1px solid #bae6fd;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(14, 165, 233, 0.08) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const SubtitleBadge = styled.span`
  background-color: #e0f2fe;
  color: #0284c7;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  border: 1px solid #bae6fd;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Title = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
  color: #0f172a;

  span {
    background: linear-gradient(135deg, #0284c7, #4f46e5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media(max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: #475569;
  line-height: 1.6;
  max-width: 650px;
  margin: 0;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 10px;
  flex-wrap: wrap;
  justify-content: center;

  @media(max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #0ea5e9, #4f46e5);
  color: #ffffff;
  border: none;
  padding: 16px 36px;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 10px 25px -5px rgba(14, 165, 233, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 30px -5px rgba(14, 165, 233, 0.5);
  }

  @media(max-width: 480px) {
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  background-color: #ffffff;
  color: #0284c7;
  border: 1px solid #cbd5e1;
  padding: 16px 32px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  transition: background-color 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    background-color: #f8fafc;
    border-color: #0ea5e9;
    transform: translateY(-2px);
  }

  @media(max-width: 480px) {
    width: 100%;
  }
`;

const TrustIndicators = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
  color: #64748b;
  font-size: 0.9rem;
  flex-wrap: wrap;
  justify-content: center;

  span {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #334155;
    font-weight: 500;
  }
`;

// --- COMPONENT IMPLEMENTATION ---

export default function LandingCTASection() {
  const handleNavigateToAcademy = () => {
    // Redirects user straight to your Elexdon landing page route
    window.location.href = '/trainingprograms'; 
  };

  const handleScrollToPrograms = () => {
    // Scrolls smoothly to the programs section
    const programsSection = document.getElementById('programs');
    if (programsSection) {
      programsSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/trainingprograms';
    }
  };

  return (
    <CTAWrapper>
      <ContentContainer>
        <SubtitleBadge>Elexdon Digital Academy Trainings</SubtitleBadge>
        
        <Title>
          OUR TRAINING<span>PROGRAMS</span>
        </Title>
        
        <Description>
          Whether you want to build cutting-edge web applications online or master AI and Data Science in our physically or online, Elexdon Digital Academy has a track built for your success.
        </Description>
        
        <ActionButtonGroup>
          <PrimaryButton onClick={handleNavigateToAcademy}>
            Explore All Training Programs
          </PrimaryButton>
          <SecondaryButton onClick={handleScrollToPrograms}>
            View Course Curriculum
          </SecondaryButton>
        </ActionButtonGroup>

        <TrustIndicators>
          <span>🔒 Secure Registration</span>
          <span>⚡ Instant Access</span>
          <span>💼 Career Support Included</span>
        </TrustIndicators>
      </ContentContainer>
    </CTAWrapper>
  );
}

